import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { AiOutlineEdit } from "react-icons/ai";
import { Delete, BarChart } from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import Toaster from "../../components/Toast";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { addData, deleteData, getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import CreateSurvey from "./CreateSurvey";
import SurveyResultsModal from "./SurveyResultsModal";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const rewardLabel = (s) => {
  const type = (s?.rewardType || "NONE").toUpperCase();
  if (type === "NONE" || !s?.rewardValue) return "—";
  return `${s.rewardValue} ${type === "CASH" ? "Cash" : "Bonus"}`;
};

const Survey = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAllData(endPoints.api.SURVEY_ALL);
      if (res?.status === "success" || res?.status === "SUCCESS") {
        setRows(res.data || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (row) => {
    try {
      const res = await addData(
        `${endPoints.api.SURVEY_ACTIVE}${row.id}/${!row.active}`,
        {}
      );
      const body = res?.data || res;
      if (body?.status === "success" || body?.status === "SUCCESS") {
        SuccesToast(body?.message || "Updated.");
        load();
      } else {
        ErrorToast(body?.message || "Could not update the survey.");
      }
    } catch (e) {
      ErrorToast("Could not update the survey.");
    }
  };

  const remove = async (row) => {
    // Deleting a survey removes its responses too -- players who answered keep
    // the reward already credited, but the tally is gone for good.
    if (!window.confirm(`Delete "${row.title || row.question}" and all its responses?`)) {
      return;
    }
    try {
      const res = await deleteData(`${endPoints.api.SURVEY_DELETE}${row.id}`);
      const body = res?.data || res;
      if (body?.status === "success" || body?.status === "SUCCESS") {
        SuccesToast(body?.message || "Survey deleted.");
        load();
      } else {
        ErrorToast(body?.message || "Could not delete the survey.");
      }
    } catch (e) {
      ErrorToast("Could not delete the survey.");
    }
  };

  return (
    <div>
      <Toaster />
      {loading && <Loader spinner={true} visible={loading} />}

      <div className="d-flex justify-content-between align-items-center">
        <PageHeader title="Survey" />
        <Button
          variant="primary"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          Create survey
        </Button>
      </div>

      {/* Not Bootstrap's text-muted -- that is a dark grey meant for light
          backgrounds and is unreadable on this theme. */}
      <p style={{ fontSize: 13, marginTop: -8, color: "rgba(255,255,255,0.6)" }}>
        Players see the newest active survey inside its date window — one at a time.
      </p>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Reward</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell align="center">Responses</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No surveys yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.title || "—"}</TableCell>
                  <TableCell>{row.question}</TableCell>
                  <TableCell>{rewardLabel(row)}</TableCell>
                  <TableCell>{fmt(row.startDate)}</TableCell>
                  <TableCell>{fmt(row.endDate)}</TableCell>
                  <TableCell align="center">{row.totalResponses ?? 0}</TableCell>
                  <TableCell align="center">
                    <Form.Check
                      type="switch"
                      checked={!!row.active}
                      onChange={() => toggleActive(row)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" title="Results" onClick={() => setResultsFor(row)}>
                      <BarChart fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Edit"
                      onClick={() => {
                        setEditing(row);
                        setShowForm(true);
                      }}
                    >
                      <AiOutlineEdit />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={() => remove(row)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateSurvey
        show={showForm}
        survey={editing}
        onHide={() => setShowForm(false)}
        onSaved={load}
      />
      <SurveyResultsModal
        show={!!resultsFor}
        survey={resultsFor}
        onHide={() => setResultsFor(null)}
      />
    </div>
  );
};

export default Survey;
